/**-----------------------------------------------------------------------------
 * Global Order instance
 *
 * Main globally accessible instance which is created on page load.
 * State persists between pageloads.
 * Order's customer is bound to the Mobi2Go.Customer module. The customer is set
 * automatically when the Customer module is logged into and logged out of.
 * Order's location and method is saved as a cookie.
 */
import _ from 'lodash';
import { isPayAtTablePlatform } from './app';

const Order = (Mobi2Go.Order = Mobi2Go._class.Order(null));

Mobi2Go.Order.onMenuReady = function(callback) {
    if (Order.isReady()) {
        _.isFunction(callback) && _.defer(callback);
    } else {
        Order.once('menuReady', callback);
    }
    return Mobi2Go.Order;
};

Mobi2Go.onReady(function() {
    const { Customer, UI, Locations, MapsService, Menu } = Mobi2Go;

    /**-------------------------------------------------------------------------
     * Persist state
     */
    Menu.onReady(function() {
        Order.updateAvailableMenus();
        /**
         * When multiple default menus are available this allows
         * for setting the menu from the query string before the
         * order state loads.
         * This prevents what can appear as a UI glitch when
         * menus are rapidly switching on load.
         */
        const query_string = Mobi2Go.helpers.getQueryStringAsObject();
        setOrderMenuPropertyFromQueryString(query_string);
        Order.trigger('menuReady');

        Locations.onReady(function() {
            Order.setCustomer(Customer.get());

            Mobi2Go.app.onStateReady(function(state) {
                if (state.order) {
                    Order.set(state.order);
                } else if (Locations.hasOnlyOne()) {
                    Order.setLocation(Locations.getFirstNotHidden());
                }

                setIsAuthorisedForTesting(state.is_authorised_for_test_orders);
                authoriseTestingUsingQueryStringParameter();

                Order.setReady();

                if (
                    state.order &&
                    state.order.language !== Mobi2Go.app.language
                ) {
                    Order.save();
                }

                if (
                    isPayAtTablePlatform() &&
                    Mobi2Go.UI.page.currentPage() !== 'table-landing-screen' &&
                    !Mobi2Go.UI.page.isTransitioning()
                ) {
                    Mobi2Go.UI.trigger('showOrderEditor');
                }
            });
        });
    });

    /**-------------------------------------------------------------------------
     */
    MapsService.bind('coordinatesAvailable', function(coordinates) {
        if (!coordinates) {
            return;
        }
        Order.set({
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
        });
    });

    /**-------------------------------------------------------------------------
     */
    Order.bind('updateLocation', function(location) {
        if (location === null && Locations.hasOnlyOne()) {
            Order.setLocation(Locations.getFirstNotHidden());
        }
    });

    /**-------------------------------------------------------------------------
     * Bind to Customer api module
     */

    // Set order's customer on Customer logIn and logOut
    Customer.bind({
        logIn: Order.setCustomer,
        logOut: Order.setCustomer,
    });

    // Ensure order customer always matches global Mobi2Go.Customer
    Order.bind('updateCustomer', function(customer) {
        if (customer === null) {
            Customer._setLoggedInCustomer(null);
        } else {
            Order.setCustomer(Customer.get());
            if (Mobi2Go.app.language !== customer.getLanguage()) {
                /**
                 * @todo perhaps ask the customer if they would like to
                 * switch to a storefront in their language.
                 */
                customer.update({
                    language: Mobi2Go.app.language,
                });
            }
        }
    });

    /**-------------------------------------------------------------------------
     * Remember order's location and method with cookies
     */
    const { cookie } = Mobi2Go.helpers;
    const saved_location_id = cookie.get('MOBI2GO_APP_LOCATION_ID');
    const saved_method = cookie.get('MOBI2GO_APP_METHOD');
    const cookie_max_age = 60 * 60 * 24 * 365;

    function setLocation() {
        if (!Order.getLocation() && saved_location_id) {
            Order.setLocationById(saved_location_id);
        }
    }

    Order.bind({
        // Load location and method
        ready: setLocation,
        updateAvailableMethods(available_methods) {
            if (
                !Order.getMethod() &&
                saved_method &&
                _.includes(available_methods, saved_method)
            ) {
                Order.setMethod(saved_method);
            }
        },
        // Save location and method
        updateLocation(location) {
            if (location) {
                cookie.set(
                    'MOBI2GO_APP_LOCATION_ID',
                    location.id,
                    cookie_max_age
                );
            }
        },
        updateMethod(method) {
            if (method) {
                cookie.set('MOBI2GO_APP_METHOD', method, cookie_max_age);
            }
        },
    });

    /**-------------------------------------------------------------------------
     * Saved Address
     */
    function getLastUsedAddressId() {
        const customer_id = Customer.isLoggedIn() ? Customer.get().id : null;
        const location_id = Order.getLocation() ? Order.getLocation().id : null;

        if (customer_id && location_id) {
            const location_address_id = +cookie.get(
                `MOBI2GO_APP_ADDRESS${customer_id}${location_id}`
            );

            if (location_address_id) {
                return location_address_id;
            }

            const address_id = +cookie.get(`MOBI2GO_APP_ADDRESS${location_id}`);

            if (address_id) {
                return address_id;
            }
        }

        return null;
    }

    function setLastUsedAddressId(id) {
        const customer_id = Customer.isLoggedIn() ? Customer.get().id : null;
        const location_id = Order.getLocation() ? Order.getLocation().id : null;

        if (customer_id && location_id) {
            cookie.set(`MOBI2GO_APP_ADDRESS_${customer_id}${location_id}`, id);
            cookie.set(`MOBI2GO_APP_ADDRESS_${customer_id}`, id);
        }
    }

    Order.bind({
        updateAvailableAddresses(addresses) {
            if (Order.getAddress()) {
                return;
            }

            const address_ids = _.map(addresses, 'id');
            const last_used_address_id = getLastUsedAddressId();

            if (
                last_used_address_id &&
                _.includes(address_ids, last_used_address_id)
            ) {
                Order.setAddressById(last_used_address_id);
                return;
            }

            if (addresses.length) {
                Order.setAddressById(addresses[0].id);
            }
        },
        updateAddress(address) {
            if (address) {
                setLastUsedAddressId(address.id);
            }
        },
    });

    function setOrderMenuPropertyFromQueryString(query_string) {
        if (query_string.menu) {
            const menus = Order.getAvailableMenus();
            const menu = _.find(menus, m => {
                return m.name.toLowerCase() === query_string.menu.toLowerCase();
            });

            if (menu) {
                Order.setMenu(menu);
            }
        }
    }

    /**-------------------------------------------------------------------------
     * Set order properties via query string
     */
    function setOrderPropertiesFromQueryString() {
        /**
         * If customer was away and the order got abandoned we need
         * to wait for the order to be reset before we can set
         * the properties from the query string.
         */
        if (Order.getProgressPhase() === 'abandoned') {
            Order.once(
                'updateProgressPhase',
                setOrderPropertiesFromQueryString
            );
            return;
        }
        if (Order.getProgressPhase() !== 'unconfirmed') {
            return;
        }

        const query_string = Mobi2Go.helpers.getQueryStringAsObject();

        if (query_string.location) {
            Order.setLocationByName(query_string.location);
        }

        if (query_string.store) {
            Order.setLocationByName(query_string.store);
        }

        if (query_string.location_id) {
            Order.setLocationById(query_string.location_id);
        }

        if (query_string.method) {
            Order.setMethod(query_string.method);
        }

        if (query_string.voucher_code) {
            Order.setVoucherCode(query_string.voucher_code);
        }

        if (query_string.table_id) {
            Order.setTableId(query_string.table_id);
        }

        setOrderMenuPropertyFromQueryString(query_string);
    }
    Order.onReady(setOrderPropertiesFromQueryString);

    /**
     * The first time a customer loads a storefront
     * their order is not persisted until they
     * at least interact with it once. (adding product, logging in, ...).
     *
     * Some features like custom html need the order to be persisted to
     * be available.
     *
     * This will ensure that an order always exists for a given session.
     */
    function automaticallyCreateOrder() {
        if (!Order.getId()) {
            Order.save();
        }
    }
    Order.onReady(automaticallyCreateOrder);
});

let is_authorised_for_testing = false;

function setIsAuthorisedForTesting(is_authorised) {
    if (is_authorised_for_testing === is_authorised) {
        return;
    }

    is_authorised_for_testing = !!is_authorised;

    Order.trigger('updateAuthorisedForTesting', is_authorised_for_testing);
}

Order.isAuthorisedForTesting = () => is_authorised_for_testing;

Order.authoriseTesting = key => {
    return new Promise((resolve, reject) => {
        if (Order.isAuthorisedForTesting()) {
            resolve(true);
        }

        Mobi2Go.app.serverRequest({
            api_version: 1,
            endpoint: ['storefronts', 'session', 'test_order_auth'],
            type: 'POST',
            data: {
                key,
            },
            success() {
                setIsAuthorisedForTesting(true);
                resolve(true);
            },
            error() {
                reject();
            },
        });
    });
};

Order.unauthoriseTesting = () => {
    return new Promise((resolve, reject) => {
        if (!Order.isAuthorisedForTesting()) {
            resolve(true);
        }

        Mobi2Go.app.serverRequest({
            api_version: 1,
            endpoint: ['storefronts', 'session', 'test_order_auth'],
            type: 'DELETE',
            success() {
                setIsAuthorisedForTesting(false);
                resolve(true);
            },
            error() {
                reject();
            },
        });
    });
};

function authoriseTestingUsingQueryStringParameter() {
    const { test_order_key } = Mobi2Go.helpers.getQueryStringAsObject();
    if (Order.isAuthorisedForTesting()) {
        if (!test_order_key) {
            Mobi2Go.Order.unauthoriseTesting();
        }
        return;
    }
    if (!test_order_key) {
        return;
    }

    Order.authoriseTesting(test_order_key)
        .then(() => {
            if (Order.isAuthorisedForTesting()) {
                Order.setIsTest(true);
            }
        })
        .catch(() => {
            if (console.warn) {
                console.warn('request to authorise test orders failed');
            }
        });
}
